import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Icon from "@ats/src/components/shared/Icon";
import Button from "@ats/src/components/shared/Button";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import { PlatoChip } from "@ats/src/components/shared/PlatoMark";
import BetaTag from "@ats/src/components/shared/BetaTag";
import PlatoHeaderNavButton from "@ats/src/components/shared/PlatoHeaderNavButton";
import { FeatureFlipper } from "@ats/src/components/shared/FeatureFlipper";
import PlatoSummary, { PlatoSummaryData } from "@ats/src/views/jobApplications/Plato/PlatoSummary";
import PlatoTabEmptyState from "@ats/src/views/jobApplications/Plato/PlatoTabEmptyState";
import PlatoLoadingState from "@ats/src/views/jobApplications/Plato/PlatoLoadingState";
import {
  useGenerateAiSummary,
  useAiJobApplicationSummary,
} from "@shared/queryHooks/useAiJobApplicationSummary";
import { useOrganizationAiCreditBalance } from "@shared/queryHooks/useOrganizationAiCreditBalance";
import { useToastContext } from "@shared/context/ToastContext";
import { useModalContext } from "@shared/context/ModalContext";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import AlertModal from "@ats/src/components/modals/AlertModal";
import ConfirmationModal from "@ats/src/components/modals/ConfirmationModal";
import { distanceInWords } from "@shared/lib/time";


interface Props {
  jobApplication: any;
  history: any;
  match: any;
  isFetchingJobApplication?: boolean;
}

function PlatoTab({ jobApplication, history, match, isFetchingJobApplication }: Props) {
  const [showPlatoLoading, setShowPlatoLoading] = React.useState(false);
  const addToast = useToastContext();
  const { currentOrganizationUser } = useCurrentSession();
  const { openModal, removeModal } = useModalContext();
  const { mutate: generate, isLoading: isGenerating } = useGenerateAiSummary();
  const {
    data: creditData,
    isError: creditError,
    isLoading: isLoadingCredits,
  } = useOrganizationAiCreditBalance();

  const summaryStatus = jobApplication.aiJobApplicationSummaryStatus;
  const statusValue = summaryStatus?.status;

  const { data: fullSummary, isFetching: isFetchingSummary } = useAiJobApplicationSummary({
    jobApplicationId: jobApplication.id,
    aiJobApplicationSummaryId: summaryStatus?.aiJobApplicationSummaryId,
  });

  const fullSummaryStatus = fullSummary?.status;
  const regenerationSettled = statusValue === "regenerating" || fullSummaryStatus === "failed";
  if (showPlatoLoading && regenerationSettled) setShowPlatoLoading(false);
  const hasContent = statusValue === "current" || statusValue === "regenerating";
  const structuredData = hasContent ? fullSummary?.structuredData : null;
  const totalRemaining = creditError ? 0 : creditData?.totalCreditsRemaining || 0;
  const buttonLoading = isLoadingCredits || isGenerating;

  window.logger("%c[PlatoTab] render", "color: #1976D2", {
    jobApplicationId: jobApplication.id,
    summaryStatus,
    statusValue,
    fullSummary,
    fullSummaryStatus,
    structuredData,
    creditData,
    totalRemaining,
    showPlatoLoading,
  });

  const handleGenerate = () => {
    generate(
      { jobApplicationId: jobApplication.id },
      {
        onSuccess: () => {
          setShowPlatoLoading(true);
        },
        onError: (error: any) => {
          addToast({
            title: error?.data?.errors?.general?.[0] || "Failed to queue summary",
            kind: "warning",
            delay: 10000,
          });
        },
      },
    );
  };

  const handleClickRegenerate = () => {
    const modal = (
      <ConfirmationModal
        title="Are you sure you want to regenerate?"
        subcopy={
          <>
            Regenerating runs a fresh analysis of this candidate and replaces the existing review.
            Uses 1 credit of {totalRemaining} remaining.
            <br />
            <br />
            Recommended when the resume has been updated or the job description has changed.
          </>
        }
        confirmText="Regenerate"
        cancelText="Cancel"
        onConfirm={() => {
          removeModal();
          handleGenerate();
        }}
        onCancel={() => {
          removeModal();
        }}
        isDestructiveConfirm={false}
      />
    );

    openModal(modal);
  };

  const handleBuyCredits = () => {
    openModal(
      <AlertModal
        title="Only admins can buy credits"
        subcopy="Buying credits requires admin access, which your account doesn't have. To get more credits, ask an admin to purchase them."
        confirmText="Close"
        onConfirm={removeModal}
        onCancel={removeModal}
      />,
    );
  };

  const handleNavigateToResumeTab = () => {
    history.push(`${match.url.replace(/\/[^/]+$/, "")}/resume`);
  };

  const deriveEmptyStatus = () => {
    if (!isLoadingCredits && totalRemaining <= 0) return "noCredits";
    return "ready";
  };

  let body: React.ReactNode;
  if ((statusValue === "current" || statusValue === "regenerating") && fullSummary) {
    const assessment = structuredData?.assessment;
    const summaryData: PlatoSummaryData = {
      headline: summaryStatus?.headline || "",
      roleFit: fullSummary?.integratedRoleAnalysis || structuredData?.roleAnalysis || fullSummary?.summaryText || "",
      scorePct: summaryStatus?.scorePercentage || 0,
      generatedAgo: distanceInWords(summaryStatus?.updatedAt),
      criteria: fullSummary?.criteriaResults || [],
      standoutAccomplishments: assessment?.standoutAccomplishments || [],
      applicableExperience: structuredData?.applicableExperience,
      gaps: structuredData?.gaps,
      skills: structuredData?.skills || [],
      keySkills: assessment?.keySkills || [],
      primaryDomain: assessment?.primaryDomain?.name,
      secondaryDomain: assessment?.secondaryDomain?.name,
      stale: fullSummary?.stale,
    };
    body = <Styled.Content><PlatoSummary data={summaryData} /></Styled.Content>;
  } else if ((statusValue === "current" || statusValue === "regenerating") && isFetchingSummary) {
    // Status record references a review, but the full record is still loading — show the
    // loader instead of flickering the empty/ready state over an existing review.
    body = (
      <Styled.EmptyStateWrapper>
        <LoadingIndicator label="Loading..." />
      </Styled.EmptyStateWrapper>
    );
  } else if (
    showPlatoLoading ||
    fullSummaryStatus === "pending" ||
    fullSummaryStatus === "textract_processing" ||
    fullSummaryStatus === "extracting" ||
    fullSummaryStatus === "summarizing" ||
    fullSummaryStatus === "awaiting_job_criteria" ||
    fullSummaryStatus === "scoring" ||
    fullSummaryStatus === "integrating" ||
    fullSummaryStatus === "retrying" ||
    (fullSummaryStatus === "succeeded" && !structuredData)
  ) {
    body = (
      <Styled.EmptyStateWrapper>
        <PlatoLoadingState status={fullSummaryStatus} />
      </Styled.EmptyStateWrapper>
    );
  } else if (jobApplication.bulkAiSummaryProcessing) {
    body = (
      <Styled.EmptyStateWrapper>
        <PlatoTabEmptyState
          status="bulkQueued"
          creditsRemaining={totalRemaining}
          isAdmin={currentOrganizationUser.isAdmin}
          onClick={handleGenerate}
        />
      </Styled.EmptyStateWrapper>
    );
  } else if (fullSummary && fullSummaryStatus === "failed") {
    body = (
      <Styled.EmptyStateWrapper>
        <PlatoTabEmptyState
          status="failed"
          creditsRemaining={totalRemaining}
          isAdmin={currentOrganizationUser.isAdmin}
          onClick={handleGenerate}
          failedDueToNoJobCriteria={fullSummary.failedDueToNoJobCriteria}
          jobId={jobApplication.jobId}
        />
      </Styled.EmptyStateWrapper>
    );
  } else if ((!statusValue || statusValue === "none") && !jobApplication.hasResume && isFetchingJobApplication) {
    // jobApplication is seeded with possibly-stale store data (initialData) and is still being
    // fetched — don't flash "needs a resume" before hasResume is known; show the loader.
    body = (
      <Styled.EmptyStateWrapper>
        <LoadingIndicator label="Loading..." />
      </Styled.EmptyStateWrapper>
    );
  } else if ((!statusValue || statusValue === "none") && !jobApplication.hasResume) {
    body = (
      <Styled.EmptyStateWrapper>
        <PlatoTabEmptyState
          status="noResume"
          onClick={handleNavigateToResumeTab}
        />
      </Styled.EmptyStateWrapper>
    );
  } else {
    body = (
      <Styled.EmptyStateWrapper>
        <PlatoTabEmptyState
          status={deriveEmptyStatus()}
          creditsRemaining={totalRemaining}
          isAdmin={currentOrganizationUser.isAdmin}
          onClick={handleGenerate}
        />
      </Styled.EmptyStateWrapper>
    );
  }

  let headerRight: React.ReactNode = null;
  if (statusValue === "regenerating") {
    headerRight = (
      <Styled.Regenerating>
        <PlatoChip size={22} radius={6} variant="sparkle" spinning />
        Regenerating
      </Styled.Regenerating>
    );
  } else if (statusValue === "current" && fullSummary?.stale) {
    if (isLoadingCredits || totalRemaining > 0) {
      headerRight = (
        <Button styleType="text" onClick={handleClickRegenerate} loading={buttonLoading} disabled={buttonLoading}>
          <Styled.ButtonLabel><Icon name="refresh-cw" /> Regenerate</Styled.ButtonLabel>
        </Button>
      );
    } else if (currentOrganizationUser.isAdmin) {
      headerRight = (
        <PlatoHeaderNavButton
          label="Buy credits"
          onClick={() =>
            history.push({
              pathname: "/hire/settings/plato-ai/billing",
              state: { orginalPathname: history.location.pathname },
            })
          }
        />
      );
    } else {
      headerRight = (
        <PlatoHeaderNavButton label="Buy credits" onClick={handleBuyCredits} />
      );
    }
  }

  return (
    <FeatureFlipper feature="AI_APPLICANT_SUMMARY">
      <Styled.Container>
        <Styled.Header hasContent={hasContent}>
          <Styled.HeaderLeft>
            <PlatoChip size={26} radius={7} />
            <span>Plato</span>
            <BetaTag variant="filledDarkOnly" />
          </Styled.HeaderLeft>
          <Styled.HeaderRight>
            {headerRight}
          </Styled.HeaderRight>
        </Styled.Header>
        <Styled.Body>{body}</Styled.Body>
      </Styled.Container>
    </FeatureFlipper>
  );
}

export default PlatoTab;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Regenerating = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_Regenerating;
    display: inline-flex;
    align-items: center;
    gap: ${t.spacing[2]};
    ${[t.text.sm, t.text.semibold]}
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
  `;
});

Styled.Container = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_Container;
    display: flex;
    flex-direction: column;
    height: 100%;
  `;
});

Styled.Header = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_Header;
    ${[t.pt(4), t.pb(4), t.px(4)]}
    border-bottom: ${props.hasContent
      ? `1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]}`
      : `none`};
    display: flex;
    justify-content: space-between;
    align-items: center;
  `;
});

Styled.HeaderLeft = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_HeaderLeft;
    ${t.text.h2}
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
    color: ${t.dark ? t.color.gray[200] : t.color.black};
  `;
});

Styled.HeaderRight = styled.div`
  label: PlatoTab_HeaderRight;
  display: flex;
  align-items: center;
`;

Styled.ButtonLabel = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_ButtonLabel;
    display: inline-flex;
    align-items: center;
    gap: ${t.spacing[1]};
  `;
});

Styled.Body = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_Body;
    flex-grow: 1;
    overflow-y: auto;
  `;
});

Styled.Content = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_Content;
    ${t.p(5)}
  `;
});

Styled.EmptyStateWrapper = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoTab_EmptyStateWrapper;
    ${[t.mx(4), t.mt("px")]}
  `;
});

